import { BASE_URL, environment } from "utils/constant";
import { LuUserMinus } from "react-icons/lu";
import article from "../../assets/permissions/article.svg";
import group from "../../assets/permissions/group.svg";
import finance from "../../assets/permissions/credit_score.svg";
import { IoCheckbox } from "react-icons/io5";
import { MdCheckBoxOutlineBlank } from "react-icons/md";
import { useAlert } from "../../components/UI_components/Alert";
import React, { useState, useEffect } from "react";
import { X, Plus, Check, User } from "lucide-react";
import { rtdb } from "utils/firebase";
import { getInitials } from "utils/tasks/taskHelpers";

export const AddMemberModal = ({ onClose, onSave, teams = [] }) => {
  const [organizationMembers, setOrganizationMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showPermissions, setShowPermissions] = useState(false);
  const [disablePermissions, setDisablePermissions] = useState(false);
  const { addAlert } = useAlert();

  const [permissions, setPermissions] = useState({
    is_admin: false,
    view_material_management: true,
    view_worker_management: true,
    view_finance: true,
    view_all: false,
    edit_material_management: false,
    edit_worker_management: false,
    edit_finance: false,
    edit_all: false,
    create_taskgroup: false,
    report_generation: false,
    member_control: false,
    joined_as: "member",
  });

  useEffect(() => {
    fetchOrganizationMembers();
  }, []);

  const fetchOrganizationMembers = async () => {
    try {
      setLoading(true);
      const accessToken = localStorage.getItem("access");
      const organizationId = JSON.parse(
        localStorage.getItem("organization")
      )?.id;

      const response = await fetch(
        `${BASE_URL}/org/organization/${organizationId}/members/`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch organization members");
      }

      const data = await response.json();
      if (data.status && data.data) {
        // Filter out members who are already in the team
        const filteredMembers = data.data.filter((member) => {
          // Check if this member's ID is not in the teams array
          return !teams.some(
            (teamMember) => teamMember.organization_member_id === member.id
          );
        });
        // console.log("Filtered members:", filteredMembers);
        setOrganizationMembers(filteredMembers);
      } else {
        throw new Error("Invalid data format");
      }
    } catch (error) {
      setError(error.message);
      addAlert("Error fetching members", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleMemberSelect = (member) => {
    setSelectedMember(member);
    setPermissions((prev) => ({
      ...prev,
      joined_as: member?.user?.profession || member?.profession,
    }));
    setShowPermissions(true);
  };

  const handlePermissionChange = (key) => {
    let updatedPermissions = { ...permissions };

    if (key === "is_admin") {
      const newAdminVal = !permissions.is_admin;
      if (newAdminVal) {
        // When admin is checked, set all permissions to true and disable editing
        updatedPermissions = {
          ...permissions,
          is_admin: true,
          view_material_management: true,
          view_worker_management: true,
          view_finance: true,
          view_all: true,
          edit_material_management: true,
          edit_worker_management: true,
          edit_finance: true,
          edit_all: true,
          create_taskgroup: true,
          report_generation: true,
          member_control: true,
        };
        setDisablePermissions(true);
      } else {
        // When admin is unchecked, keep current permissions but enable editing
        updatedPermissions = {
          ...permissions,
          is_admin: false,
        };
        setDisablePermissions(false);
      }
    } else if (key === "view_all" && !disablePermissions) {
      const newVal = !permissions.view_all;
      updatedPermissions = {
        ...permissions,
        view_all: newVal,
        view_material_management: newVal,
        view_worker_management: newVal,
        view_finance: newVal,
      };
    } else if (key === "edit_all" && !disablePermissions) {
      const newVal = !permissions.edit_all;
      updatedPermissions = {
        ...permissions,
        edit_all: newVal,
        edit_material_management: newVal,
        edit_worker_management: newVal,
        edit_finance: newVal,
        create_taskgroup: newVal,
        report_generation: newVal,
        member_control: newVal,
      };
    } else {
      // Only allow editing if not admin
      if (!permissions.is_admin) {
        // toggle individual permission
        updatedPermissions[key] = !permissions[key];
        // maintain view_all / edit_all flags accordingly
        updatedPermissions.view_all =
          updatedPermissions.view_material_management &&
          updatedPermissions.view_worker_management &&
          updatedPermissions.view_finance;

        updatedPermissions.edit_all =
          updatedPermissions.edit_material_management &&
          updatedPermissions.edit_worker_management &&
          updatedPermissions.edit_finance &&
          updatedPermissions.create_taskgroup &&
          updatedPermissions.report_generation &&
          updatedPermissions.member_control;
      }
    }

    setPermissions(updatedPermissions);
  };

  const handleAddMember = async () => {
    try {
      const accessToken = localStorage.getItem("access");
      const siteId = localStorage.getItem("site");

      const requestBody = {
        ...permissions,
        organization_member: selectedMember.id,
        sites: parseInt(siteId),
      };

      const response = await fetch(`${BASE_URL}/team/add-member/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error("Failed to add member");
      }

      addAlert("Member added successfully!", "success");
      onSave && onSave();
      onClose();
    } catch (error) {
      setError(error.message);
      addAlert("Error adding member", "error");
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center"
      style={{ zIndex: 1000 }}
    >
      <div
        className="bg-[#F5F5F5] rounded-2xl w-full max-w-xl h-[98vh]"
        style={{
          overflowY: "auto",
        }}
      >
        {/* Header */}
        <div className="flex items-center bg-white justify-between p-4 border-b rounded-t-2xl">
          <button onClick={onClose} className="p-1">
            <X className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-semibold">
            {showPermissions ? "Set Permissions" : "Add Member"}
          </h2>
          <div className="p-1 w-5 h-5"></div> {/* Empty div for balance */}
        </div>

        {/* Content */}
        <div className="p-4">
          {!showPermissions ? (
            // Member selection view
            <div>
              <div className="bg-white rounded-lg p-4 mb-4">
                <h3 className="font-medium text-gray-800 mb-2">
                  Organization Members
                </h3>
                <p className="text-sm text-gray-600 mb-2">
                  Select a member to add to your team
                </p>
                {/* <div className="relative mb-2">
                  <input
                    type="text"
                    placeholder="Search members..."
                    className="w-full px-4 py-2 border rounded-lg"
                  />
                </div> */}
              </div>

              <div className="space-y-2 h-[60vh]" style={{ overflowY: "auto" }}>
                {loading ? (
                  <div className="text-center py-4">Loading members...</div>
                ) : error ? (
                  <div className="text-center py-4 text-red-500">{error}</div>
                ) : organizationMembers.length === 0 ? (
                  <div className="text-center py-4">
                    {" "}
                    Please add organization members from Global Dashboard to add
                    them in your projects.
                  </div>
                ) : (
                  organizationMembers.map((member) => (
                    <div
                      key={member.id}
                      className="flex flex-col p-3 bg-white hover:bg-gray-50 rounded-lg cursor-pointer"
                      onClick={() => handleMemberSelect(member)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {member.user.userpic ? (
                            <img
                              src={member.user.userpic}
                              alt={`${member.user.first_name} ${member.user.last_name}`}
                              className="w-10 h-10 rounded-full"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center">
                              <User className="w-6 h-6 text-gray-600" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium">
                              {member.user.first_name} {member.user.last_name}
                            </p>
                            <p className="text-sm text-gray-500">
                              {member.user.email}
                            </p>
                          </div>
                        </div>
                        <div
                          className="rounded-2xl flex flex-row text-xs font-medium text-black items-center justify-center px-3 py-1"
                          style={{
                            background: member.is_admin ? "#C8E3CA" : "#FFFF8C",
                          }}
                        >
                          {member.is_admin
                            ? "Admin"
                            : member?.user?.profession || member?.profession}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : (
            // Permissions setting view
            <div className="space-y-6">
              {/* Selected member info */}
              <div className="bg-white rounded-lg p-4 mb-6">
                <h3 className="text-black font-bold text-[14px] mb-4">
                  Selected Member
                </h3>
                <div className="flex items-center gap-3">
                  {selectedMember.user.userpic ? (
                    <img
                      src={selectedMember.user.userpic}
                      alt={`${selectedMember.user.first_name} ${selectedMember.user.last_name}`}
                      className="w-14 h-14 rounded-full"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-gray-300 flex items-center justify-center">
                      <User className="w-8 h-8 text-gray-600" />
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-base">
                      {selectedMember.user.first_name}{" "}
                      {selectedMember.user.last_name}
                    </p>
                    <p className="text-sm text-gray-500">
                      {selectedMember.user.email}
                    </p>
                    <p className="text-sm text-gray-500">
                      (
                      {selectedMember.is_admin
                        ? "Admin"
                        : selectedMember?.user?.profession || "Member"}
                      )
                    </p>
                  </div>
                </div>
              </div>

              {/* Role selection */}
              <div className="rounded p-3 bg-white">
                <h2 className="text-black font-bold">Role</h2>
                <p className="text-sm text-[#232323] mb-4">
                  Define member role in team
                </p>

                <div className="mb-4">
                  <label className="flex items-center justify-between p-2">
                    <span className="font-medium text-[13px]">Admin</span>
                    {permissions.is_admin ? (
                      <IoCheckbox
                        className="w-5 h-5 rounded text-purple-900"
                        onClick={() => handlePermissionChange("is_admin")}
                      />
                    ) : (
                      <MdCheckBoxOutlineBlank
                        className="w-5 h-5 rounded text-purple-900"
                        onClick={() => handlePermissionChange("is_admin")}
                      />
                    )}
                  </label>

                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Role
                    </label>
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100">
                      {selectedMember?.user?.profession || "Member"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Task Authority */}
              <div className="rounded p-3 bg-white">
                <label className="flex items-center justify-between p-2">
                  <span className="capitalize font-medium text-[13px]">
                    Task Authority
                  </span>
                  {permissions.create_taskgroup ? (
                    <IoCheckbox
                      className="w-5 h-5 rounded text-purple-900"
                      onClick={() =>
                        !disablePermissions &&
                        setPermissions({
                          ...permissions,
                          create_taskgroup: false,
                        })
                      }
                    />
                  ) : (
                    <MdCheckBoxOutlineBlank
                      className="w-5 h-5 rounded text-purple-900"
                      onClick={() =>
                        !disablePermissions &&
                        setPermissions({
                          ...permissions,
                          create_taskgroup: true,
                        })
                      }
                    />
                  )}
                </label>
              </div>

              {/* Report Generation */}
              <div className="rounded p-3 bg-white">
                <label className="flex items-center justify-between p-2">
                  <span className="capitalize font-medium text-[13px]">
                    Report Generation
                  </span>
                  {permissions.report_generation ? (
                    <IoCheckbox
                      className="w-5 h-5 rounded text-purple-900"
                      onClick={() =>
                        setPermissions({
                          ...permissions,
                          report_generation: false,
                        })
                      }
                    />
                  ) : (
                    <MdCheckBoxOutlineBlank
                      className="w-5 h-5 rounded text-purple-900"
                      onClick={() =>
                        setPermissions({
                          ...permissions,
                          report_generation: true,
                        })
                      }
                    />
                  )}
                </label>
              </div>

              {/* View permissions */}
              <div className="rounded p-3 bg-white">
                <h2 className="text-black font-bold">View Permissions</h2>
                <p className="text-sm text-[#232323] mb-4">
                  Control what the member can view
                </p>

                <label className="flex items-center justify-between p-2">
                  <span className="capitalize font-medium text-[13px]">
                    All
                  </span>
                  {permissions.view_all ? (
                    <IoCheckbox
                      className="w-5 h-5 rounded text-purple-900"
                      onClick={() => handlePermissionChange("view_all")}
                    />
                  ) : (
                    <MdCheckBoxOutlineBlank
                      className="w-5 h-5 rounded text-purple-900"
                      onClick={() => handlePermissionChange("view_all")}
                    />
                  )}
                </label>

                <label className="flex items-center justify-between p-2">
                  <span className="capitalize font-medium text-[13px]">
                    Material management
                  </span>
                  {permissions.view_material_management ? (
                    <IoCheckbox
                      className="w-5 h-5 rounded text-purple-900"
                      onClick={() =>
                        handlePermissionChange("view_material_management")
                      }
                    />
                  ) : (
                    <MdCheckBoxOutlineBlank
                      className="w-5 h-5 rounded text-purple-900"
                      onClick={() =>
                        handlePermissionChange("view_material_management")
                      }
                    />
                  )}
                </label>

                <label className="flex items-center justify-between p-2">
                  <span className="capitalize font-medium text-[13px]">
                    Worker management
                  </span>
                  {permissions.view_worker_management ? (
                    <IoCheckbox
                      className="w-5 h-5 rounded text-purple-900"
                      onClick={() =>
                        handlePermissionChange("view_worker_management")
                      }
                    />
                  ) : (
                    <MdCheckBoxOutlineBlank
                      className="w-5 h-5 rounded text-purple-900"
                      onClick={() =>
                        handlePermissionChange("view_worker_management")
                      }
                    />
                  )}
                </label>

                <label className="flex items-center justify-between p-2">
                  <span className="capitalize font-medium text-[13px]">
                    Finance
                  </span>
                  {permissions.view_finance ? (
                    <IoCheckbox
                      className="w-5 h-5 rounded text-purple-900"
                      onClick={() => handlePermissionChange("view_finance")}
                    />
                  ) : (
                    <MdCheckBoxOutlineBlank
                      className="w-5 h-5 rounded text-purple-900"
                      onClick={() => handlePermissionChange("view_finance")}
                    />
                  )}
                </label>
              </div>

              {/* Edit permissions */}
              <div className="rounded p-3 bg-white">
                <h2 className="text-black font-bold">Edit Permissions</h2>
                <p className="text-sm text-[#232323] mb-4">
                  Control what the member can edit
                </p>

                <label className="flex items-center justify-between p-2">
                  <span className="capitalize font-medium text-[13px]">
                    All
                  </span>
                  {permissions.edit_all ? (
                    <IoCheckbox
                      className="w-5 h-5 rounded text-purple-900"
                      onClick={() => handlePermissionChange("edit_all")}
                    />
                  ) : (
                    <MdCheckBoxOutlineBlank
                      className="w-5 h-5 rounded text-purple-900"
                      onClick={() => handlePermissionChange("edit_all")}
                    />
                  )}
                </label>

                <label className="flex items-center justify-between p-2">
                  <span className="capitalize font-medium text-[13px]">
                    Material management
                  </span>
                  {permissions.edit_material_management ? (
                    <IoCheckbox
                      className="w-5 h-5 rounded text-purple-900"
                      onClick={() =>
                        handlePermissionChange("edit_material_management")
                      }
                    />
                  ) : (
                    <MdCheckBoxOutlineBlank
                      className="w-5 h-5 rounded text-purple-900"
                      onClick={() =>
                        handlePermissionChange("edit_material_management")
                      }
                    />
                  )}
                </label>

                <label className="flex items-center justify-between p-2">
                  <span className="capitalize font-medium text-[13px]">
                    Worker management
                  </span>
                  {permissions.edit_worker_management ? (
                    <IoCheckbox
                      className="w-5 h-5 rounded text-purple-900"
                      onClick={() =>
                        handlePermissionChange("edit_worker_management")
                      }
                    />
                  ) : (
                    <MdCheckBoxOutlineBlank
                      className="w-5 h-5 rounded text-purple-900"
                      onClick={() =>
                        handlePermissionChange("edit_worker_management")
                      }
                    />
                  )}
                </label>

                <label className="flex items-center justify-between p-2">
                  <span className="capitalize font-medium text-[13px]">
                    Finance
                  </span>
                  {permissions.edit_finance ? (
                    <IoCheckbox
                      className="w-5 h-5 rounded text-purple-900"
                      onClick={() => handlePermissionChange("edit_finance")}
                    />
                  ) : (
                    <MdCheckBoxOutlineBlank
                      className="w-5 h-5 rounded text-purple-900"
                      onClick={() => handlePermissionChange("edit_finance")}
                    />
                  )}
                </label>

                <label className="flex items-center justify-between p-2">
                  <span className="capitalize font-medium text-[13px]">
                    Member control
                  </span>
                  {permissions.member_control ? (
                    <IoCheckbox
                      className="w-5 h-5 rounded text-purple-900"
                      onClick={() => handlePermissionChange("member_control")}
                    />
                  ) : (
                    <MdCheckBoxOutlineBlank
                      className="w-5 h-5 rounded text-purple-900"
                      onClick={() => handlePermissionChange("member_control")}
                    />
                  )}
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Button */}
        <div className="p-4">
          {showPermissions ? (
            <div className="flex gap-4">
              <button
                onClick={() => setShowPermissions(false)}
                className="w-1/2 bg-gray-200 text-gray-800 font-semibold rounded-lg py-3"
              >
                Back
              </button>
              <button
                onClick={handleAddMember}
                className="w-1/2 bg-[#3B0069] text-white font-semibold rounded-lg py-3 flex items-center justify-center gap-2"
              >
                Add Member
                <div className="rounded-full p-1 flex bg-text-clr flex-row items-center">
                  <Check className="w-4 h-4" />
                </div>
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
export const TeamsModal = ({ onClose, teams = [], onMemberClick }) => {
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);

  const [gpsEnabled, setGpsEnabled] = useState(false);
  const setSiteGPSPreference = async (siteId, enabled) => {
    try {
      await rtdb
        .ref(`GPSPreferences-${environment}/Sites/${siteId}`)
        .set(enabled);
      return true;
    } catch (error) {
      console.error("Error setting GPS preference:", error);
      return false;
    }
  };

  const getSiteGPSPreference = async (siteId) => {
    try {
      const snapshot = await rtdb
        .ref(`GPSPreferences-${environment}/Sites/${siteId}`)
        .once("value");
      const enabled = snapshot.val();
      return enabled !== null ? enabled : false; // Default to false if not set
    } catch (error) {
      console.error("Error getting GPS preference:", error);
      return false;
    }
  };
  useEffect(() => {
    const fetchGPSPreference = async () => {
      const siteId = localStorage.getItem("site");
      const enabled = await getSiteGPSPreference(siteId);
      setGpsEnabled(enabled);
    };

    fetchGPSPreference();
  }, []);

  const handleGPSToggle = async () => {
    const siteId = localStorage.getItem("site");
    const newGpsEnabled = !gpsEnabled;
    setGpsEnabled(newGpsEnabled);
    await setSiteGPSPreference(siteId, newGpsEnabled);
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center"
      style={{
        zIndex: 1000,
      }}
    >
      <div className="bg-[#F5F5F5] rounded-2xl w-full max-w-xl ">
        {/* Header */}
        <div className="flex items-center bg-white justify-between p-4 border-b rounded-t-2xl">
          <button onClick={onClose} className="p-1">
            <X className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-semibold">Teams</h2>
          <div className="p-1 w-5 h-5"></div>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Team count and GPS toggle */}
          <div className="bg-white rounded-lg p-4 flex justify-between items-center mb-6">
            <p className="text-gray-700">{teams.length} members in your team</p>
            <div className="flex items-center gap-2"></div>
          </div>

          {/* Team members list */}
          <div
            className="space-y-4 h-[56vh]"
            style={{
              overflowY: "auto",
            }}
          >
            {teams.map((member) => (
              <div
                key={member.member_id}
                className="flex flex-col py-3 px-3 bg-white hover:bg-gray-50 rounded-lg cursor-pointer"
                onClick={() => onMemberClick(member)}
              >
                <div className="flex items-center pb-3 justify-between border-b-[0.5px] border-[#A3A3A3] ">
                  <div className="flex items-center gap-3">
                    {member?.userpic ? (
                      <img
                        src={member.userpic}
                        alt={`${member.first_name} ${member.last_name}`}
                        className="w-10 h-10 rounded-full"
                      />
                    ) : (
                      <User className="w-10 h-10 rounded-full bg-gray-300" />
                    )}
                    <div>
                      <p className="font-medium">
                        {member.first_name} {member.last_name}
                      </p>
                      <p className="text-sm text-gray-500">
                        +91-{member.contact_no}
                      </p>
                    </div>
                  </div>
                  <div className="w-5 h-5"></div>
                </div>
                <div className="flex items-center justify-start gap-3 mt-2">
                  <div
                    className="rounded-2xl flex flex-row text-[15px] font-medium text-black items-center justify-center px-3 py-1"
                    style={{
                      background: member?.is_admin ? "#C8E3CA" : "#FFFF8C",
                    }}
                  >
                    {member.is_admin
                      ? "Admin"
                      : member?.user?.profession || member?.profession}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Add member button */}
        <div className="p-4">
          <button
            onClick={() => setShowAddMemberModal(true)}
            className="w-full bg-[#3B0069] text-white font-semibold rounded-lg py-3 flex items-center justify-center gap-2"
          >
            Add Member
            <div className="rounded-full p-1 flex bg-text-clr flex-row items-center">
              <Plus className="w-4 h-4" />
            </div>
          </button>
        </div>
        {showAddMemberModal && (
          <AddMemberModal
            onClose={() => setShowAddMemberModal(false)}
            teams={teams}
            onSave={() => {
              onClose();
            }}
          />
        )}
      </div>
    </div>
  );
};

export const AccessManagementModal = ({ member, onClose, onSave }) => {
  const { addAlert } = useAlert();
  const ICONS = {
    is_admin: article,
    view_all: article,
    view_material_management: group,
    view_worker_management: group,
    view_finance: finance,
    edit_all: article,
    edit_material_management: group,
    edit_worker_management: group,
    edit_finance: finance,
    create_taskgroup: article,
    report_generation: article,
  };

  const [permissions, setPermissions] = useState({
    is_admin: member.is_admin,
    view_all: member.view_all,
    view_material_management: member.view_material_management,
    view_worker_management: member.view_worker_management,
    view_finance: member.view_finance,
    edit_all: member.edit_all,
    edit_material_management: member.edit_material_management,
    edit_worker_management: member.edit_worker_management,
    edit_finance: member.edit_finance,
    create_taskgroup: member.create_taskgroup,
    report_generation: member.report_generation,
  });

  // Show alert when trying to modify permissions while admin is enabled
  const showAdminAlert = () => {
    addAlert(
      "Please disable Admin permission first to modify individual permissions",
      "warning"
    );
  };

  // Centralized permission toggle handler:
  // - toggles individual view/edit keys
  // - when any individual view/edit is turned OFF, clear view_all/edit_all
  // - when all individual view/edit keys are ON, set view_all/edit_all
  const handlePermissionToggle = (key) => {
    // Prevent changing individual permissions while admin is enabled
    if (permissions.is_admin && key !== "is_admin") {
      showAdminAlert();
      return;
    }

    // Toggle view_all
    if (key === "view_all") {
      const newVal = !permissions.view_all;
      setPermissions({
        ...permissions,
        view_all: newVal,
        view_material_management: newVal,
        view_worker_management: newVal,
        view_finance: newVal,
      });
      return;
    }

    // Toggle edit_all
    if (key === "edit_all") {
      const newVal = !permissions.edit_all;
      setPermissions({
        ...permissions,
        edit_all: newVal,
        edit_material_management: newVal,
        edit_worker_management: newVal,
        edit_finance: newVal,
        create_taskgroup: newVal,
        report_generation: newVal,
        member_control: newVal,
      });
      return;
    }

    // Individual view_* toggles
    if (key.startsWith("view_")) {
      setPermissions((prev) => {
        const next = { ...prev, [key]: !prev[key] };
        // If turned off, unset view_all
        if (!next[key]) {
          next.view_all = false;
        } else {
          // If all view_* are true, set view_all
          if (
            next.view_material_management &&
            next.view_worker_management &&
            next.view_finance
          ) {
            next.view_all = true;
          }
        }
        return next;
      });
      return;
    }

    // Individual edit_* toggles
    if (key.startsWith("edit_")) {
      setPermissions((prev) => {
        const next = { ...prev, [key]: !prev[key] };
        // If turned off, unset edit_all
        if (!next[key]) {
          next.edit_all = false;
        } else {
          // If all edit-related keys true, set edit_all
          const editKeys = [
            "edit_material_management",
            "edit_worker_management",
            "edit_finance",
            "create_taskgroup",
            "report_generation",
            "member_control",
          ];
          if (editKeys.every((k) => next[k])) {
            next.edit_all = true;
          }
          // Ensure corresponding view permission is enabled when enabling an edit permission
          if (key !== "edit_all") {
            const viewKey = `view_${key.replace("edit_", "")}`;
            if (next[key]) next[viewKey] = true;
          }
        }
        return next;
      });
      return;
    }
  };

  //   format date to 02:25PM, 05 July, 2023 format
  const formatDate = (date) => {
    const d = new Date(date);
    const hours = d.getHours();
    const minutes = d.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const formattedDate = `${hours % 12}:${minutes} ${ampm}, ${d.getDate()} ${months[d.getMonth()]
      }, ${d.getFullYear()}`;
    return formattedDate;
  };

  const handleSave = async () => {
    try {
      const accessToken = localStorage.getItem("access");
      const requestBody = { ...permissions };
      requestBody.sites = localStorage.getItem("site");
      requestBody.organization_member = member?.organization_member_id;
      const response = await fetch(
        `${BASE_URL}/team/update-member/${member.member_id}/`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(requestBody),
        }
      );
      if (response.ok) {
        addAlert("Success!", "success");
        onSave();
        onClose();
      }
    } catch (error) {
      console.error("Error updating permissions:", error);
    }
  };

  const handleRemoveMember = async () => {
    try {
      const accessToken = localStorage.getItem("access");
      const response = await fetch(
        `${BASE_URL}/team/delete-member/${member.member_id}/`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      if (response.ok) {
        onSave();
        onClose();
      }
    } catch (error) {
      console.error("Error removing member:", error);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center"
      style={{
        zIndex: 50,
      }}
    >
      <div className="bg-[#F5F5F5] rounded-2xl w-[50%] ">
        <div className="flex  bg-white items-center justify-between p-4 border-b rounded-t-2xl">
          <button onClick={onClose} className="p-1">
            <X className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-semibold">Access Management</h2>
          <div className="p-1 w-5 h-5"></div>
        </div>

        <div
          className="px-12 py-6 h-[70vh]"
          style={{
            overflowY: "auto",
          }}
        >
          {/* Employee details */}
          <div className="mb-8 px-6 pt-3 bg-white rounded-xl">
            <h3 className="text-black font-bold text-[14px] mb-4">
              Employee details
            </h3>
            <div className="flex items-center pb-6 border-b-[1px] border-[#CCCCCC] justify-between">
              <div className="flex items-center gap-3">
                {member.userpic ? (
                  <img
                    src={member.userpic}
                    alt={`${member.first_name} ${member.last_name}`}
                    className="w-14 h-14 rounded-full"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-gray-300 flex items-center justify-center">
                    <span className="text-[#3B0069] text-xl font-medium">
                      {getInitials(`${member.first_name} ${member.last_name}`)}
                    </span>
                  </div>
                )}
                <div>
                  <p className="font-semibold text-base">
                    {member.first_name} {member.last_name}
                  </p>
                  <p className="text-sm text-gray-500">{member?.contact_no}</p>
                  <p className="text-sm text-gray-500">
                    (
                    {member.is_admin
                      ? "Admin"
                      : member?.user?.profession || member?.profession}
                    )
                  </p>
                </div>
              </div>
              <button
                onClick={handleRemoveMember}
                style={{
                  display: member.joined_as === "owner" ? "none" : "flex",
                }}
                className="bg-[#F9E8E8] w-40 py-2 flex flex-row items-center gap-2 justify-center rounded-xl hover:bg-red-50"
              >
                <p className="text-[#C21313] text-[12px] font-bold ">
                  Remove
                </p>
                <LuUserMinus className="w-4 h-4 text-[#C21313]" />
              </button>
            </div>
            <div className="flex flex-row pt-4 py-4 items-center justify-between">
              <p className="text-[14px] font-normal text-[#737373]">
                Joined / Added on
              </p>
              <p className="text-[14px] font-medium text-black">
                {formatDate(member.created_on)}
              </p>
            </div>
          </div>

          {/* Permissions */}
          <div className="space-y-6">
            {/* Admin section */}
            <div className="rounded p-3 bg-white">
              <label className="flex items-center justify-between p-2">
                <div className="flex flex-row items-start gap-2">
                  <div className="bg-[#C5B3D2] rounded-full p-1">
                    <img
                      src={ICONS["is_admin"]}
                      alt="icon"
                      className="w-5 h-5"
                    />
                  </div>
                  <div className="flex flex-col mt-1 items-start gap-2">
                    <span className="capitalize font-medium text-[13px]">
                      Admin
                    </span>
                    <p className="text-xs text-gray-500">
                      Full access to all features and settings
                    </p>
                  </div>
                </div>
                {permissions.is_admin ? (
                  <IoCheckbox
                    className="w-5 h-5 rounded text-purple-900"
                    onClick={() =>
                      setPermissions({
                        ...permissions,
                        is_admin: false,
                      })
                    }
                  />
                ) : (
                  <MdCheckBoxOutlineBlank
                    className="w-5 h-5 rounded text-purple-900"
                    onClick={() =>
                      setPermissions({
                        is_admin: true,
                        // every permission set to true
                        create_taskgroup: true,
                        report_generation: true,
                        view_all: true,
                        edit_all: true,
                        view_finance: true,
                        view_material_management: true,
                        view_worker_management: true,
                        edit_finance: true,
                        edit_material_management: true,
                        edit_worker_management: true,
                      })
                    }
                  />
                )}
              </label>
            </div>

            {/* Task Authority section */}
            <div className="rounded p-3 bg-white">
              <label className="flex items-center justify-between p-2">
                <div className="flex flex-row items-start gap-2">
                  <div className="bg-[#C5B3D2] rounded-full p-1">
                    <img
                      src={ICONS["create_taskgroup"]}
                      alt="icon"
                      className="w-5 h-5"
                    />
                  </div>
                  <div className="flex flex-col mt-1 items-start gap-2">
                    <span className="capitalize font-medium text-[13px]">
                      Task Authority
                    </span>
                    <p className="text-xs text-gray-500">
                      Create and manage task groups
                    </p>
                  </div>
                </div>
                {permissions.create_taskgroup ? (
                  <IoCheckbox
                    className="w-5 h-5 rounded text-purple-900"
                    onClick={() => {
                      if (permissions.is_admin) {
                        showAdminAlert();
                        return;
                      }
                      setPermissions({
                        ...permissions,
                        create_taskgroup: false,
                      });
                    }}
                  />
                ) : (
                  <MdCheckBoxOutlineBlank
                    className="w-5 h-5 rounded text-purple-900"
                    onClick={() =>
                      !permissions.is_admin &&
                      setPermissions({
                        ...permissions,
                        create_taskgroup: true,
                      })
                    }
                  />
                )}
              </label>
            </div>

            {/* Report Generation section */}
            <div className="rounded p-3 bg-white">
              <label className="flex items-center justify-between p-2">
                <div className="flex flex-row items-start gap-2">
                  <div className="bg-[#C5B3D2] rounded-full p-1">
                    <img
                      src={ICONS["report_generation"]}
                      alt="icon"
                      className="w-5 h-5"
                    />
                  </div>
                  <div className="flex flex-col mt-1 items-start gap-2">
                    <span className="capitalize font-medium text-[13px]">
                      Report Generation
                    </span>
                    <p className="text-xs text-gray-500">
                      Generate and access site reports
                    </p>
                  </div>
                </div>
                {permissions.report_generation ? (
                  <IoCheckbox
                    className="w-5 h-5 rounded text-purple-900"
                    onClick={() => {
                      if (permissions.is_admin) {
                        showAdminAlert();
                        return;
                      }
                      setPermissions({
                        ...permissions,
                        report_generation: false,
                      });
                    }}
                  />
                ) : (
                  <MdCheckBoxOutlineBlank
                    className="w-5 h-5 rounded text-purple-900"
                    onClick={() =>
                      !permissions.is_admin &&
                      setPermissions({
                        ...permissions,
                        report_generation: true,
                      })
                    }
                  />
                )}
              </label>
            </div>

            {/* View section */}
            <div className="rounded p-3 bg-white">
              <h2 className="text-black font-bold">View</h2>
              <p className="text-sm text-[#232323] mb-4">
                control what the member can view
              </p>
              {Object.entries(permissions)
                .filter(([key]) => key.startsWith("view_"))
                .map(([key, value]) => (
                  <label key={key} className="flex items-center justify-between p-2">
                    <div className="flex flex-row items-center gap-2">
                      <div className="bg-[#C5B3D2] rounded-full p-1">
                        <img src={ICONS[key]} alt="icon" className="w-5 h-5" />
                      </div>
                      <span className="capitalize font-medium text-[13px]">
                        {key.replace("view_", "").replace("_", " ")}
                      </span>
                    </div>
                    {value ? (
                      <IoCheckbox
                        className="w-5 h-5 rounded text-purple-900"
                        onClick={() => handlePermissionToggle(key)}
                      />
                    ) : (
                      <MdCheckBoxOutlineBlank
                        className="w-5 h-5 rounded text-purple-900"
                        onClick={() => handlePermissionToggle(key)}
                      />
                    )}
                  </label>
                ))}
            </div>

            {/* Edit section */}
            <div className="rounded p-3 bg-white">
              <h2 className="text-black font-bold">Edit</h2>
              <p className="text-sm text-[#232323] mb-4">
                control what the member can edit
              </p>
              {Object.entries(permissions)
                .filter(([key]) => key.startsWith("edit_"))
                .map(([key, value]) => (
                  <label key={key} className="flex items-center justify-between p-2">
                    <div className="flex flex-row items-center gap-2">
                      <div className="bg-[#C5B3D2] rounded-full p-1">
                        <img src={ICONS[key]} alt="icon" className="w-5 h-5" />
                      </div>
                      <span className="capitalize font-medium text-[13px]">
                        {key === "create_taskgroup"
                          ? "Task Authority"
                          : key === "report_generation"
                            ? "Report Generation"
                            : key.replace("edit_", "").replace("_", " ")}
                      </span>
                    </div>
                    {value ? (
                      <IoCheckbox
                        className="w-5 h-5 rounded text-purple-900"
                        onClick={() => handlePermissionToggle(key)}
                      />
                    ) : (
                      <MdCheckBoxOutlineBlank
                        className="w-5 h-5 rounded text-purple-900"
                        onClick={() => handlePermissionToggle(key)}
                      />
                    )}
                  </label>
                ))}
            </div>
          </div>
          {/* Save button */}
          <div className="p-4 border-t">
            <button
              onClick={handleSave}
              className="w-full bg-[#3B0069] text-white font-semibold rounded-lg py-3 flex items-center justify-center gap-2"
            >
              Save changes
              <div className="flex flex-row items-center p-1 rounded-full bg-text-clr">
                <Check className="w-4 h-4" />
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
